import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { syncGifts } from '@theme/lucid-gifts';

/**
 * Lucid: Anteil des kleinsten Rabattcodes, den es im Shop gibt (heute 10 %:
 * WILLKOMMEN10, optional LUCIDBACK10, siehe KLAVIYO_FLOWS.md; die
 * Wartelisten-Codes haben 15 bis 50 %). Codes gelten auf die ganze Bestellung
 * - genauer: auf alle Zeilen, fuer die sie freigegeben sind (siehe unten).
 *
 * Shopify behaelt von Code und Mengenrabatt den, der mehr spart. Ein Code
 * verliert also nur, wenn der Mengenrabatt mindestens diesen Anteil der
 * Summe ausmacht, auf die der Code wirken wuerde. Mit den Stufen aus PREISE.md
 * (4 Dosen 3,79 %, 5 Dosen 7,64 %, 6 Dosen 10,2 %) ist das ab sechs Dosen der
 * Fall. Darunter heisst `applicable: false` in einem reinen Einmalkauf-
 * Warenkorb immer: Code ungueltig, abgelaufen, aufgebraucht oder fuer diesen
 * Warenkorb nicht freigegeben - dann waere "Dein Mengenrabatt ist hier besser"
 * als erster Grund falsch.
 *
 * Abo (Franz, "Option B", 12.10.2026): Die 15-%-Wartelisten-Codes gelten NUR
 * fuer Einmalkaeufe (appliesOnSubscription=false). 25/30/40 % gelten fuer
 * Einmalkauf und die erste Abo-Bestellung, Secret Club (50 %) fuer jede
 * Abo-Lieferung, WILLKOMMEN10 fuer beides. Ein reiner Abo-Warenkorb liefert
 * fuer einen gueltigen 15-%-Code deshalb `applicable: false` - genau wie fuer
 * einen erfundenen. Im gemischten Warenkorb rabattiert so ein Code nur die
 * Einmalkauf-Zeilen und verliert gegen den Mengenrabatt schon frueher - aber
 * nie, weil er "nur fuer Einmalkaeufe" gilt.
 *
 * Kommt ein Code unter 10 % dazu, diesen Wert senken.
 */
const SMALLEST_CODE_SHARE = 0.1;

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorVolume - The error element for a code next to the volume discount.
 * @property {HTMLElement} cartDiscountErrorSubscription - The error element for a code in a cart with subscription items.
 * @property {HTMLElement} cartDiscountErrorSubscriptionVolume - The error element for a code in a cart with subscription items and a volume discount that can win.
 * @property {HTMLElement} cartDiscountErrorSubscriptionOtherCode - The error element for a code in a cart with subscription items and another code already applied.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = [
    'cartDiscountError',
    'cartDiscountErrorDiscountCode',
    'cartDiscountErrorVolume',
    'cartDiscountErrorSubscription',
    'cartDiscountErrorSubscriptionVolume',
    'cartDiscountErrorSubscriptionOtherCode',
    'cartDiscountErrorShipping',
  ];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      cartDiscountError.classList.add('hidden');
      for (const element of Object.values(this.#errorTexts())) element.classList.add('hidden');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        // Lucid: `applicable: false` heisst "Code ungueltig", "ein anderer Code
        // gilt schon", "Code verliert gegen den Mengenrabatt" oder "Code gilt
        // nicht fuers Abo" - die Antwort unterscheidet das nicht. Die Meldung
        // nennt jeden Grund, der in diesem Warenkorb moeglich ist (siehe
        // #rejectedCodeError und den Kommentar in snippets/cart-summary.liquid).
        this.#handleDiscountError(this.#rejectedCodeError(data, discountCodeValue));
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      // Lucid: Wartelisten-Geschenke (assets/lucid-gifts.js) abgleichen,
      // BEVOR der Rest des Warenkorbs vom neuen Code erfaehrt. Sonst rendert
      // component-cart-items.js auf das DiscountUpdateEvent hin die Section
      // neu, waehrend die Dose gerade dazukommt, und dieser spaetere Render
      // ohne Dose kann den mit Dose ueberholen.
      const gifts = await syncGifts({ cart: data, sections: [this.dataset.sectionId] });
      const finalData = gifts.changed && gifts.cart ? gifts.cart : data;
      const finalHtml = (gifts.changed && gifts.sections[this.dataset.sectionId]) || newHtml;

      document.dispatchEvent(new DiscountUpdateEvent(finalData, this.id));
      morphSection(this.dataset.sectionId, finalHtml);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      // Lucid: Ohne den Code fallen die Geschenke wieder heraus - wie beim
      // Eingeben vor allen anderen abgleichen (siehe applyDiscount).
      const gifts = await syncGifts({ cart: data, sections: [this.dataset.sectionId] });
      const finalData = gifts.changed && gifts.cart ? gifts.cart : data;
      const finalHtml =
        (gifts.changed && gifts.sections[this.dataset.sectionId]) || data.sections[this.dataset.sectionId];

      document.dispatchEvent(new DiscountUpdateEvent(finalData, this.id));
      morphSection(this.dataset.sectionId, finalHtml);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * The error texts, one per error type.
   */
  #errorTexts() {
    const {
      cartDiscountErrorDiscountCode,
      cartDiscountErrorVolume,
      cartDiscountErrorSubscription,
      cartDiscountErrorSubscriptionVolume,
      cartDiscountErrorSubscriptionOtherCode,
      cartDiscountErrorShipping,
    } = this.refs;
    return {
      discount_code: cartDiscountErrorDiscountCode,
      volume: cartDiscountErrorVolume,
      subscription: cartDiscountErrorSubscription,
      subscription_volume: cartDiscountErrorSubscriptionVolume,
      subscription_other_code: cartDiscountErrorSubscriptionOtherCode,
      shipping: cartDiscountErrorShipping,
    };
  }

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'volume' | 'subscription' | 'subscription_volume' | 'subscription_other_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    this.refs.cartDiscountError.classList.remove('hidden');
    this.#errorTexts()[type].classList.remove('hidden');
  }

  /**
   * Lucid: Welche Meldung bekommt ein Code, den /cart/update.js mit
   * `applicable: false` zurueckliefert? Die Antwort sagt nicht, warum. Moegliche
   * Gruende, je nach Warenkorb:
   *
   * 1. Code ungueltig, abgelaufen, aufgebraucht - immer moeglich.
   * 2. Ein anderer Code gilt schon. Die Wartelisten-Codes kombinieren sich mit
   *    keinem anderen Code (ORDER-Klasse, `combinesWith.orderDiscounts: false`,
   *    LAUNCH_AUDIT_2026-08.md); Shopify behaelt den, der mehr spart. Der
   *    gueltige Code steht dann mit `applicable: true` in derselben Antwort.
   *    Weil dieser Code den Mengenrabatt schon geschlagen hat, ist Grund 3 in
   *    dem Fall nie der einzige.
   * 3. Der automatische Mengenrabatt ("Bau deine Box", Appstle-Discount-
   *    Function der Klasse PRODUCT) spart mehr. Codes sind Bestellrabatte und
   *    nicht damit kombinierbar; Shopify behaelt den, der mehr spart. Moeglich,
   *    wenn der Mengenrabatt mindestens SMALLEST_CODE_SHARE der Summe ausmacht,
   *    auf die ein Code wirken wuerde.
   * 4. Code gilt nur fuer Einmalkaeufe (15-%-Wartelisten-Codes) - moeglich NUR
   *    im reinen Abo-Warenkorb. Im gemischten rabattiert so ein Code die
   *    Einmalkauf-Zeilen; abgelehnt wird er dort nur aus Grund 1 bis 3.
   *    "Gilt nur fuer Einmalkaeufe" waere dort ein Grund, den es nicht geben
   *    kann - und schickt den Kunden aus dem Abo.
   *
   * Basis fuer 3: im gemischten Warenkorb nur die Einmalkauf-Zeilen, weil ein
   * Einmalkauf-Code nur dort wirkt und gegen den Mengenrabatt auf ALLEN Zeilen
   * antritt (Beispiel: 5 Dosen Abo + 1 Dose einmalig - 15 % auf die eine Dose
   * sparen weniger als die Staffel auf alle sechs). Codes fuer beides wirken
   * auf die groessere Summe und verlieren damit seltener; die kleinere Basis
   * deckt beide ab. Sonst die ganze Zwischensumme VOR Zeilenrabatten
   * (`final_line_price` ist schon um sie gekuerzt, also wieder aufschlagen,
   * wie in snippets/cart-summary.liquid).
   *
   * Abo-Zeilen erkennt die AJAX-API an `selling_plan_allocation`. Als
   * Einmalkauf-Zeile zaehlt nur, was vor Zeilenrabatten etwas kostet: eine
   * Gratis-Zeile kann kein Code rabattieren, sie macht aus einem Abo-Warenkorb
   * keinen gemischten.
   *
   * Nicht auf `type === 'automatic'` pruefen: am 12.10.2026 gemessen liefert
   * die AJAX-API fuer den Mengenrabatt `discount_application.type: ""`. Sicher
   * ist nur die Abgrenzung zu Codes (`'discount_code'`).
   *
   * @param {{ discount_codes?: Array<{ code: string; applicable: boolean }>, items?: Array<{ final_line_price: number, selling_plan_allocation?: object | null, line_level_discount_allocations?: Array<{ amount: number; discount_application?: { type?: string } }> }> }} cart
   * @param {string} rejectedCode - The code that came back with `applicable: false`.
   * @returns {'discount_code' | 'volume' | 'subscription' | 'subscription_volume' | 'subscription_other_code'}
   */
  #rejectedCodeError(cart, rejectedCode) {
    const items = cart.items ?? [];
    /** @param {typeof items[number]} item */
    const isSubscription = (item) => Boolean(item.selling_plan_allocation);
    /** @param {typeof items[number]} item */
    const automaticOf = (item) =>
      (item.line_level_discount_allocations ?? []).reduce(
        (sum, allocation) =>
          allocation.amount > 0 && allocation.discount_application?.type !== 'discount_code'
            ? sum + allocation.amount
            : sum,
        0
      );
    /** @param {typeof items[number]} item */
    const baseOf = (item) => item.final_line_price + automaticOf(item);

    const hasSubscription = items.some(isSubscription);
    const oneTimeItems = items.filter((item) => !isSubscription(item) && baseOf(item) > 0);
    const subscriptionOnly = hasSubscription && oneTimeItems.length === 0;

    // Grund 2 geht vor: Gilt schon ein anderer Code, hat er den Mengenrabatt
    // geschlagen, und "maximaler Rabatt bereits erreicht" ist der Grund.
    const otherCodeApplies = (cart.discount_codes ?? []).some(
      (discount) => discount.code !== rejectedCode && discount.applicable === true
    );
    if (otherCodeApplies) return subscriptionOnly ? 'subscription_other_code' : 'discount_code';

    const codeBaseItems = hasSubscription && !subscriptionOnly ? oneTimeItems : items;
    const automatic = items.reduce((sum, item) => sum + automaticOf(item), 0);
    const codeBase = codeBaseItems.reduce((sum, item) => sum + baseOf(item), 0);
    const volumeCanWin = automatic > 0 && codeBase > 0 && automatic / codeBase >= SMALLEST_CODE_SHARE;

    if (subscriptionOnly) return volumeCanWin ? 'subscription_volume' : 'subscription';
    return volumeCanWin ? 'volume' : 'discount_code';
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
